import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as moment from 'moment/moment';
import PropTypes from 'prop-types';
import axios from 'axios';
import OrganizationDirectSelling from '../../../commonComponents/propTypes/OrganizationDirectSelling';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import TreeHeaderBarFilters, {
  ORIENTATION,
} from '../components/TreeHeaderBarFilters';
import translate from '../../../commonComponents/translate';
import TeamStructureUserCard from '../structure/TeamStructureUserCard';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Loader from '../../../services/loader';
import Notifications from '../../../services/notifications';
import withCSRF from '../../../commonComponents/withCSRF';
import { compose } from '../../../services/utilityFunctions';
import i18n from '../../../services/i18n';
import { RECURRENCE_INTERVAL_CUSTOM } from '../../../services/dateUtilityFunctions';

function SponsorTree(props) {
  const { t, _csrf } = props;

  const { loggedUser } = useContext(LoggedUserContext);
  const { organization = {}, tenant = {} } = useContext(ConfigsContext);
  const { directSellingSettings = {} } = tenant?.settings || {};
  const { binaryIntegration } = directSellingSettings || {};
  const { directSelling = {} } = organization || {};
  const { recurrence } = directSelling || {};

  // region Initial Configs
  const startOfMonth = moment().utc().startOf('month').valueOf();
  const startOfWeek = moment().utc().clone().startOf('isoWeek').valueOf();
  const today = moment().endOf('day').valueOf();
  const initialState = {
    startDate:
      recurrence === OrganizationDirectSelling.RECURRENCE.WEEKLY
        ? startOfWeek
        : startOfMonth,
    endDate: today,
    search: '',
    rangeType: RECURRENCE_INTERVAL_CUSTOM,
  };
  const loader = useRef(new Loader('#treeContainers'));
  // endregion Initial Configs

  // region Use State
  const [filters, setFilters] = useState(initialState);
  const [orientation, setOrientation] = useState(ORIENTATION.HORIZONTAL);
  const [usersPerLevel, setUsersPerLevel] = useState([]);
  const [selectedUserPerLevel, setSelectedUserPerLevel] = useState([
    { level: 1, user: loggedUser },
  ]);
  const [selectedUserDetails, setSelectedUserDetails] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchUser, setSelectedSearchUser] = useState(null);
  // endregion Use State

  const unselectUser = useCallback(() => {
    setSelectedUserDetails('');
  }, [setSelectedUserDetails]);

  // region handleStateChange
  const handleFilters = useCallback(
    field => value => {
      switch (field) {
        case 'search':
          setFilters(prevState => ({ ...prevState, [field]: value }));
          break;
        default:
          console.log('**** FILTERS DEFAULT ****', field, value);
          break;
      }
    },
    [setFilters],
  );
  const handleDateChange = useCallback(
    (startDate, endDate, rangeType) => {
      setFilters(prevState => ({
        ...prevState,
        startDate,
        endDate,
        rangeType,
      }));
    },
    [setFilters],
  );
  const handleOrientation = useCallback(
    type => setOrientation(type),
    [setOrientation],
  );

  const handleClearFilters = useCallback(
    () => setFilters(initialState),
    [setFilters],
  );
  // endregion handleStateChange

  // region Aux functions
  const isCardSelected = (level, user) => {
    const found = selectedUserPerLevel.find(
      it => it.level === level && it.user._id === user._id,
    );
    return found != null;
  };
  // endregion Aux functions

  // region Handle the Flow of Cards
  const fetchUsersByTier = async (
    userId,
    search,
    startDate,
    endDate,
    direction = 'down',
  ) => {
    try {
      const endpoint = `/api/user/direct-selling/members/${filters.rangeType}`;

      const params = {
        bySponsor: true,
        user: userId,
      };
      const isCustom = filters?.rangeType === RECURRENCE_INTERVAL_CUSTOM;
      if (search) params.search = search;
      if (isCustom && startDate) params.startDate = startDate;
      if (isCustom && endDate) params.endDate = endDate;
      params.direction = direction;
      const { data: response } = await axios.get(endpoint, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
        },
      });
      return response.data;
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
      return [];
    }
  };

  const handleSelectSearchUser = useCallback(user => {
    setSelectedSearchUser(user);
    setUsersPerLevel([{ level: 1, users: [user] }]);
    setSelectedUserPerLevel([]);
  }, []);

  const handleClickUserCard = async (level, user, direction = 'down') => {
    setLoading(true);
    try {
      const users = await fetchUsersByTier(
        user._id,
        null,
        filters.startDate,
        filters.endDate,
        direction,
      );

      if (direction === 'up') {
        // Keep all existing levels, prepend the new one before level 1
        const newLevel = {
          level: 0,
          users,
        };

        // Re-index existing levels upward
        const updatedList = [
          newLevel,
          ...usersPerLevel.map(l => ({ ...l, level: l.level + 1 })),
        ];

        const updatedSelected = [
          ...selectedUserPerLevel.map(l => ({ ...l, level: l.level + 1 })),
          { level: 1, user }, // the clicked user shifts to level 1
        ];

        setUsersPerLevel(updatedList);
        setSelectedUserPerLevel(updatedSelected);
      } else {
        // 'down' direction.
        //
        // We find the actual array index of the clicked card (level numbers
        // can be non-contiguous after "up" navigations, e.g. 0, 2, 3 instead
        // of 1, 2, 3). slice(0, clickedIndex + 1) keeps every column UP TO
        // AND INCLUDING the clicked card, then appends the new level below.
        // This means the clicked ancestor card always stays visible.
        const clickedIndex = usersPerLevel.findIndex(l => l.level === level);
        const list = [
          ...usersPerLevel.slice(0, clickedIndex + 1),
          { level: level + 1, users },
        ];

        let selectedList = selectedUserPerLevel;
        if (level === 1) {
          selectedList = [];
        } else {
          selectedList = selectedList.filter(l => l.level <= level - 1);
        }
        selectedList.push({
          level,
          user,
        });

        setSelectedUserPerLevel(selectedList);
        setUsersPerLevel(list);
      }

      setLoading(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
    }
  };

  const getLoggedUserPersonalVA = async () => {
    try {
      setLoading(true);
      const params = {
        sponsor: loggedUser._id,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      const { data: response } = await axios.get(
        '/api/analytics/market-place/commissions/totals',
        {
          params,
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      setLoading(false);
      return response?.data || {};
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
      setLoading(false);
      return { myVA: 0, userVA: 0 };
    }
  };
  // endregion Handle the Flow of Cards

  // region useEffect
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  useEffect(() => {
    setUsersPerLevel([]);
    setSelectedUserPerLevel([]);
    setSelectedSearchUser(null);
    setSearchResults([]);

    const fetchData = async () => {
      try {
        setLoading(true);

        if (filters.search && filters.search.length > 0) {
          const users = await fetchUsersByTier(
            loggedUser._id,
            filters.search,
            filters.startDate,
            filters.endDate,
          );
          setSearchResults(users);
        } else {
          const userTier1Info = await getLoggedUserPersonalVA();
          const users = await fetchUsersByTier(
            loggedUser._id,
            null,
            filters.startDate,
            filters.endDate,
          );

          const user = {
            ...loggedUser,
            ...userTier1Info,
            personalVA: userTier1Info?.userVA,
            myVA: userTier1Info?.myVA,
          };

          setUsersPerLevel([
            { level: 1, users: [user] },
            { level: 2, users },
          ]);
        }

        setLoading(false);
      } catch (err) {
        Notifications.showErrorFromRequest(err);
        setLoading(false);
      }
    };

    fetchData().then();
  }, [filters.startDate, filters.endDate, filters.search]);

  useEffect(() => {
    setUsersPerLevel([]);
    setSelectedUserPerLevel([]);
    setSelectedSearchUser(null);
    setSearchResults([]);
  }, [filters]);
  // endregion useEffect

  const hasSearchPanel = filters.search && searchResults.length > 0;

  return (
    <>
      <TreeHeaderBarFilters
        onDateChange={handleDateChange}
        onFiltersChange={handleFilters}
        onOrientationChange={handleOrientation}
        filters={filters}
        orientation={orientation}
        onClearFilters={handleClearFilters}
        section="sponsor"
      />
      <div id="treeContainers" className="row team-structure-container">
        {hasSearchPanel && (
          <div className="sponsor-tree-search-results">
            <div className="sponsor-tree-search-results__header">
              <span className="sponsor-tree-search-results__title">
                {t('shop.product.results')}
              </span>
            </div>
            <div className="sponsor-tree-search-results__list">
              {searchResults.map(user => (
                <TeamStructureUserCard
                  key={`searchResult_${user._id}`}
                  t={t}
                  level={1}
                  user={user}
                  isSelected={selectedSearchUser?._id === user._id}
                  userTypeNames={{}}
                  onClickCard={(lvl, u) => handleSelectSearchUser(u)}
                  directSellingSettings={directSellingSettings}
                  areDetailsVisible={false}
                  setSelectedUserDetails={setSelectedUserDetails}
                  selectedUserDetails={selectedUserDetails}
                  isSponsorTree
                  binaryIntegration={binaryIntegration}
                  filters={{ ...filters, search: '' }}
                  orientation={orientation}
                  isSomeSelectedInLevel={false}
                  onUnselectUser={unselectUser}
                />
              ))}
            </div>
          </div>
        )}
        <div
          id="connectStructureContainer"
          className={`connect-structure-container ${
            orientation === ORIENTATION.HORIZONTAL
              ? 'connect-structure-horizontal'
              : 'connect-structure-vertical'
          }`}
          style={hasSearchPanel ? { marginTop: 0 } : undefined}
        >
          <div id="wrapper-sponsor-user">
            {usersPerLevel.map(l => (
              <div className="connect-structure-level-container" key={l.level}>
                <div className="connect-structure-level">
                  <div className="connect-structure-level-title">
                    {i18n.__n('connect.teams.structure.tier_level', l.level)}
                  </div>
                  <div className="connect-structure-user-list">
                    {l.users.map(user => (
                      <TeamStructureUserCard
                        t={t}
                        level={l.level}
                        user={user}
                        isSelected={isCardSelected(l.level, user)}
                        userTypeNames={{}}
                        onClickCard={handleClickUserCard}
                        key={`teamCard_${l.level}_${user._id}`}
                        directSellingSettings={directSellingSettings}
                        areDetailsVisible={selectedUserDetails === user._id}
                        setSelectedUserDetails={setSelectedUserDetails}
                        selectedUserDetails={selectedUserDetails}
                        isSponsorTree
                        binaryIntegration={binaryIntegration}
                        filters={filters}
                        onUnselectUser={unselectUser}
                        orientation={orientation}
                        isSomeSelectedInLevel={selectedUserPerLevel.some(
                          it =>
                            it.level === l.level && it.user._id === user._id,
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

SponsorTree.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default compose(translate, withCSRF)(SponsorTree);
